import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Button, Input, TextArea, Select, FormField, Alert, Spinner } from '../ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface ProjectFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialProject?: any;
  isEditMode?: boolean;
  onProjectUpdated?: () => void;
}

interface Category {
  id: number;
  name: string;
  notes?: string;
  color?: string;
  icon?: string;
}

// Added Business interface
interface Business {
  id: number;
  name: string;
}

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ProjectForm: React.FC<ProjectFormProps> = ({ isOpen, onClose, initialProject = null, isEditMode = false, onProjectUpdated }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]); // State for businesses
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false); // Loading state for businesses
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [businessError, setBusinessError] = useState<string | null>(null); // Error state for businesses

  // Form State
  const [projectName, setProjectName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedBusiness, setSelectedBusiness] = useState<string>(''); // State for selected business
  const [notes, setNotes] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState<string | undefined>(undefined); // Optional
  const [status, setStatus] = useState('active'); // Default status changed to 'active'
  const [projectType, setProjectType] = useState('build'); // Default to Build (0→1)
  const [priority, setPriority] = useState('medium'); // Default priority
  const [tags, setTags] = useState('');

  // Submission State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingCategories(true);
      setIsLoadingBusinesses(true);
      setCategoryError(null);
      setBusinessError(null);
      try {
        const [catResponse, bizResponse] = await Promise.all([
          apiClient.get('/api/categories/').catch(err => {
            console.error('Error fetching categories:', err);
            setCategoryError('Failed to load categories. Select later if needed.');
            return { data: [] };
          }),
          apiClient.get('/api/businesses/').catch(err => {
            console.error('Error fetching businesses:', err);
            setBusinessError('Failed to load businesses. Select later if needed.');
            return { data: [] };
          })
        ]);
        setCategories(catResponse.data);
        setBusinesses(bizResponse.data);
      } catch (err) {
        console.error('Error fetching form data:', err);
      } finally {
        setIsLoadingCategories(false);
        setIsLoadingBusinesses(false);
      }
    };
    if (isOpen) {
      fetchData();
      if (isEditMode && initialProject) {
        setProjectName(initialProject.name || '');
        setSelectedCategory(initialProject.categories?.[0]?.id?.toString() || '');
        setSelectedBusiness(initialProject.business?.id?.toString() || '');
        setNotes(initialProject.notes || '');
        setStartDate(initialProject.start_date ? initialProject.start_date.split('T')[0] : getTodayDateString());
        setEndDate(initialProject.end_date ? initialProject.end_date.split('T')[0] : undefined);
        setStatus(initialProject.status || 'active');
        setProjectType(initialProject.project_type || 'build');
        setPriority(initialProject.priority || 'medium');
        setTags(initialProject.tags ? initialProject.tags.join(', ') : '');
      } else {
        setProjectName('');
        setSelectedCategory('');
        setSelectedBusiness('');
        setNotes('');
        setStartDate(getTodayDateString());
        setEndDate(undefined);
        setStatus('active');
        setProjectType('build');
        setPriority('medium');
        setTags('');
      }
      setIsSubmitting(false);
      setSubmitError(null);
    }
  }, [isOpen, isEditMode, initialProject]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    if (!projectName || !startDate || !notes) {
      setSubmitError('Project Name, Start Date, and Description are required.');
      setIsSubmitting(false);
      return;
    }
    const projectData = {
      name: projectName,
      notes: notes || '',
      start_date: startDate,
      end_date: endDate || null,
      status: status,
      project_type: projectType,
      priority: priority,
      category_ids: selectedCategory ? [parseInt(selectedCategory)] : [],
      business_write: selectedBusiness ? parseInt(selectedBusiness) : null,
      tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '')
    };
    try {
      if (isEditMode && initialProject) {
        const response = await apiClient.put(`/api/projects/${initialProject.id}/`, projectData);
        if (response.status >= 200 && response.status < 300) {
          if (onProjectUpdated) onProjectUpdated();
        } else {
          throw new Error(`Update failed with status ${response.status}`);
        }
      } else {
        const response = await apiClient.post('/api/projects/', projectData);
        if (response.status >= 200 && response.status < 300) {
          onClose();
        } else {
          throw new Error(`Creation failed with status ${response.status}`);
        }
      }
    } catch (err: any) {
      let errorMsg = 'Failed to save project. Please try again.';
      if (err.response?.data) {
        const errors = err.response.data;
        const details = Object.entries(errors).map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`).join('; ');
        if (details) errorMsg = details;
      }
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)'
          }} />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center'
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '28rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: '16px',
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16]
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size['2xl'],
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary
                  }}>
                    {isEditMode ? 'Edit Project' : 'Create New Project'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: COLORS.text.tertiary,
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0
                    }}
                    onClick={onClose}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  <FormField label="Project Name" htmlFor="title" required>
                    <Input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      id="title"
                      name="title"
                      required
                    />
                  </FormField>

                  <FormField label="Category" htmlFor="category">
                    {isLoadingCategories ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACING[8],
                        fontSize: TYPOGRAPHY.size.sm,
                        color: COLORS.text.secondary
                      }}>
                        <Spinner size="md" />
                        <span>Loading categories...</span>
                      </div>
                    ) : (
                      <>
                        <Select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          id="category"
                          name="category"
                          defaultValue=""
                        >
                          <option value="" disabled>Select a category</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id.toString()}>
                              {category.name}
                            </option>
                          ))}
                        </Select>
                        {categoryError && (
                          <div style={{ marginTop: SPACING[8] }}>
                            <Alert variant="error">
                              {categoryError}
                            </Alert>
                          </div>
                        )}
                      </>
                    )}
                  </FormField>

                  <FormField label="Business (Optional)" htmlFor="business">
                    {isLoadingBusinesses ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACING[8],
                        fontSize: TYPOGRAPHY.size.sm,
                        color: COLORS.text.secondary
                      }}>
                        <Spinner size="md" />
                        <span>Loading businesses...</span>
                      </div>
                    ) : (
                      <>
                        <Select
                          value={selectedBusiness}
                          onChange={(e) => setSelectedBusiness(e.target.value)}
                          id="business"
                          name="business"
                          defaultValue=""
                        >
                          <option value="">None</option>
                          {businesses.map((business) => (
                            <option key={business.id} value={business.id.toString()}>
                              {business.name}
                            </option>
                          ))}
                        </Select>
                        {businessError && (
                          <div style={{ marginTop: SPACING[8] }}>
                            <Alert variant="error">
                              {businessError}
                            </Alert>
                          </div>
                        )}
                      </>
                    )}
                  </FormField>

                  <FormField
                    label="Description / Notes"
                    htmlFor="notes"
                    required
                    helperText="Detailed description including purpose, key people, keywords (e.g., painting, flooring, website, SaaS) - helps AI classify emails/meetings"
                  >
                    <TextArea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      id="notes"
                      name="notes"
                      rows={4}
                      required
                      placeholder="e.g., Renovation project covering painting, flooring, cabinets. Working with contractor Euro Touch Construction..."
                    />
                  </FormField>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: SPACING[16]
                  }}>
                    <FormField label="Start Date" htmlFor="startDate" required>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        id="startDate"
                        name="startDate"
                        required
                      />
                    </FormField>
                    <FormField label="End Date" htmlFor="endDate">
                      <Input
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => setEndDate(e.target.value || undefined)}
                        id="endDate"
                        name="endDate"
                      />
                    </FormField>
                  </div>

                  <FormField label="Status" htmlFor="status" required>
                    <Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      id="status"
                      name="status"
                      required
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                      <option value="archived">Archived</option>
                    </Select>
                  </FormField>

                  <FormField label="Type" htmlFor="projectType" required>
                    <Select
                      value={projectType}
                      onChange={(e) => setProjectType(e.target.value)}
                      id="projectType"
                      name="projectType"
                      required
                    >
                      <option value="build">Build (0-1)</option>
                      <option value="run">Run (Ongoing)</option>
                    </Select>
                  </FormField>

                  <FormField label="Priority" htmlFor="priority">
                    <Select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      id="priority"
                      name="priority"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </Select>
                  </FormField>

                  <FormField label="Tags" htmlFor="tags">
                    <Input
                      type="text"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      id="tags"
                      name="tags"
                      placeholder="Separate tags with commas"
                    />
                  </FormField>

                  {/* Submission Error Display */}
                  {submitError && (
                    <Alert variant="error">
                      {submitError}
                    </Alert>
                  )}

                  <div style={{
                    marginTop: SPACING[24],
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: SPACING[12]
                  }}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={isSubmitting}
                      loadingText="Saving..."
                    >
                      Save Project
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ProjectForm;
